import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {BokkzIbDataService, Dieet, DieetAandoening, IncomeTaxEntity} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormArray, FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {CommonModule, DatePipe} from '@angular/common';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  shareReplay,
  Subscription,
  switchMap,
  take,
  tap
} from 'rxjs';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

type PGADietenFormGroup = FormGroup<{
  aandoening: FormControl<DieetAandoening | null>;
  periode: FormGroup<{
    begindatum: FormControl<Date | null>;
    einddatum: FormControl<Date | null>;
  }>;
}>;

@Component({
  selector: 'snelstart-venture-fiscaal-feature-dieten',
  templateUrl: './dieten.component.html',
  styleUrls: ['./dieten.component.scss'],
  providers: [DatePipe],
  imports: [SharedUiButtonModule, ReactiveFormsModule, SharedUiDetailsModule, SharedUiFormControlsModule, CommonModule],
})
export class DietenComponent implements OnInit, OnDestroy {
  @Input() public id!: string;
  private dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  private changeSubscriptions = new Subscription();

  public formGroup = new FormGroup({
    dieten: new FormArray<PGADietenFormGroup>([]),
  });

  public aandoeningen: SelectFormControlItem<DieetAandoening>[] = [
    {
      value: DieetAandoening.GROEIACHTERSTAND_KINDEREN_ENGERGIEVERRIJKT_MET_EIWITVERRIJKT,
      label: 'Groeiachterstand kinderen energieverrijkt met eiwitverrijkt (nr. 1)',
    },
    {
      value: DieetAandoening.GROEIACHTERSTAND_KINDEREN_ENERGIEVERRIJKT,
      label: 'Groeiachterstand kinderen energieverrijkt (nr. 2)',
    },
    {
      value: DieetAandoening.ONDERVOEDING_ENGERGIEVERRIJKT_MET_EIWITVERRIJKT,
      label: 'Ondervoeding energieverrijkt met eiwitverrijkt (nr. 1)',
    },
    {
      value: DieetAandoening.ONDERVOEDING_ENERGIEVERRIJKT,
      label: 'Ondervoeding energieverrijkt (nr. 2)',
    },
    {
      value: DieetAandoening.ONDERVOEDING_EIWITVERRIJKT,
      label: 'Ondervoeding eiwitverrijkt (nr. 3)',
    },
    {
      value: DieetAandoening.DUCUBITUS_ENGERGIEVERRIJKT_MET_EIWITVERRIJKT,
      label: 'Decubitus energieverrijkt met eiwitverrijkt (nr. 1)',
    },
    {
      value: DieetAandoening.DUCUBITUS_EIWITVERRIJKT,
      label: 'Decubitus eiwitverrijkt (nr. 3)',
    },
    {
      value: DieetAandoening.AIDS_ENGERGIEVERRIJKT_MET_EIWITVERRIJKT,
      label: 'Aids energieverrijkt met eiwitverrijkt (nr. 1)',
    },
    {
      value: DieetAandoening.COPD_ENGERGIEVERRIJKT_MET_EIWITVERRIJKT,
      label: 'Copd energieverrijkt met eiwitverrijkt (nr. 1)',
    },
    {
      value: DieetAandoening.COPD_ENERGIEVERRIJKT,
      label: 'Copd energieverrijkt (nr. 2)',
    },
    {
      value: DieetAandoening.DUMPINGSYNDROOMN_LACTOSEBEPERKT_LACTOSEVRIJ,
      label: 'Dumpingsyndroomn lactosebeperkt lactosevrij (nr. 4)',
    },
    {
      value: DieetAandoening.CHRONISCHE_PANCREATITIS_ENGERGIEVERRIJKT_MET_EIWITVERRIJKT,
      label: 'Chronische pancreatitis energieverrijkt met eiwitverrijkt (nr. 1)',
    },
    {
      value: DieetAandoening.CYSTIC_FIBROSE_ENGERGIEVERRIJKT_MET_EIWITVERRIJKT,
      label: 'Cystic fibrose energieverrijkt met eiwitverrijkt (nr. 1)',
    },
    {
      value: DieetAandoening.CYSTIC_FIBROSE_ENERGIEVERRIJKT,
      label: 'Cystic fibrose energieverrijkt (nr. 2)',
    },
    {
      value: DieetAandoening.COELIAKI_GLUTENVRIJ,
      label: 'Coeliaki glutenvrij (nr. 5)',
    },
    {
      value: DieetAandoening.COEKIALI_GLUTENVRIJ_IN_COMBINATIE_MET_LACTOSEVRIJ_LACTOSEBEPEKT,
      label: 'Coeliaki glutenvrij in combinatie met lactosevrij lactosebepekt (nr. 6)',
    },
    {
      value: DieetAandoening.SHORT_BOWEL_EMERGIEVERRIJKT_ICM_MCT_VETVERRIJKT,
      label: 'Short bowel energieverrijkt met mct vetverrijkt (nr. 7)',
    },
    {
      value: DieetAandoening.SHORT_BOWEL_EMERGIEVERRIJKT_ICM_MCT_VETVERRIJKT_MET_VITAMINEPREPARAAT,
      label: 'Short bowel energieverrijkt met mct vetverrijkt met vitaminepreparaat (nr. 8)',
    },
    {
      value: DieetAandoening.PDS_FODMAP,
      label: 'Pds fodmap (nr. 9)',
    },
    {
      value: DieetAandoening.LACTOSE_INTOLERANTIE_LACTOSEVRIJ_LACTOSEBEPERKT,
      label: 'Lactose intolerantie lactosevrij lactosebeperkt (nr. 4)',
    },
    {
      value: DieetAandoening.LEVERCIRROSE_EIWITVERRIJKT,
      label: 'Levercirrose eiwitverrijkt (nr.10)',
    },
    {
      value: DieetAandoening.OVERIGE_MAAG_LEVER_DARMZIEKTEN_ENEGRIEVERRIJKT,
      label: 'Overige maag lever darmziekten energieverrijkt (nr. 2)',
    },
    {
      value: DieetAandoening.OVERIGE_MAAG_LEVER_DARMZIEKTEN_ENEGRIEVERRIJKT_MET_VITAMINEPREPARAAT,
      label: 'Overige maag lever darmziekten energieverrijkt met vitaminepreparaat (nr.11)',
    },
    {
      value: DieetAandoening.OVERIGE_MAAG_LEVER_DARMZIEKTEN_ENEGRIEVERRIJKT_ICM_EIWITVERRIJKT,
      label: 'Overige maag lever darmziekten energieverrijkt icm eiwitverrijkt (nr. 1)',
    },
    {
      value: DieetAandoening.OVERIGE_MAAG_LEVER_DARMZIEKTEN_ENEGRIEVERRIJKT_ICM_EIWITVERRIJKT_EN_LACTOSEBEPERKT,
      label: 'Overige maag lever darmziekten energieverrijkt icm eiwitverrijkt en lactosebeperkt (nr.12)',
    },
    {
      value: DieetAandoening.HYPERCHOLESTROLEMIE_VERZADIGD_VETBEPERKT_ICM_FYTO_PLANTENSTEROLENVERRIJKT,
      label: 'Hypercholesterolemie verzadigd vetbeperkt icm fytosterolenverrijkt (nr.13)',
    },
    {
      value: DieetAandoening.VETSTOFWISELINGSSTOORNIS_VETBEPERKT_ICM_MCT_VETVERRIJKT,
      label: 'Vetstofwisselingsstoornis vetbeperkt icm mct vetverrijkt (nr.14)',
    },
    {
      value: DieetAandoening.GALACTOSEMIE_GALACTOSEBEPERKT_GALACTOSEVRIJ,
      label: 'Galactosemie galactosebeperkt galactosevrij (nr.15)',
    },
    {
      value: DieetAandoening.SACHARALE_INSOMALTASE_DEFICIENTIE_STERK_SACHAROSEBEPERKT_ICM_MALTOSEBEPERKT,
      label: 'Sacharale insomaltase deficiëntie sterk sacharosebeperkt icm (iso)maltosebeperkt (nr.16)',
    },
    {
      value: DieetAandoening.EIWITSTOFWISSELINGSSTOORNIS_STERK_EIWITBEPERKT,
      label: 'Eiwitstofwisselingsstoornis sterk eiwitbeperkt (nr.17)',
    },
    {
      value: DieetAandoening.GLYCOGEENSTAPELINGSZIEKTE_SACHAROSEBEPERKT_FRUCTOSEBEPERKT_LACTOSEBEPERKT_EN_VETBEPERKT,
      label: 'Glycogeenstapelingsziekte sacharosebeperkt fructosebeperkt lactosebeperkt en vetbeperkt (nr.18)',
    },
    {
      value: DieetAandoening.INSULINERESISTENTIE_STERK_KOOLHYDRAATBEPERKT_ZONDER_ENERGIEBEPERKING,
      label: 'Insulineresistentie sterk koolhydraatbeperkt zonder energiebeperking (nr.19)',
    },
    {
      value: DieetAandoening.CHRONISCHE_NIERINSUFFIENTIE_MET_HEMODIALYSE_EIWTITVERRIJKT_ICM_NATRIUMBEPERKT,
      label: 'Chronische nierinsufficiëntie met hemodialyse eiwitverrijkt icm natriumbeperkt (nr.10)',
    },
    {
      value: DieetAandoening.NEFROTISCH_SYNDROOM_NATRIUMBEPERKT,
      label: 'Nefrotisch syndroom natriumbeperkt (nr.20)',
    },
    {
      value: DieetAandoening.ONCOLOGIE_ENGERGIEVERRIJKT_ICM_EIWITVERRIJKT,
      label: 'Oncologie energieverrijkt icm eiwitverrijkt (nr. 1)',
    },
    {
      value: DieetAandoening.ONCOLOGIE_ENGERGIEVERRIJKT,
      label: 'Oncologie energieverrijkt (nr. 2)',
    },
    {
      value: DieetAandoening.VOEDSELOVERGEVOELIGHEID_KOEMELKEIWITVRIJ,
      label: 'Voedselovergevoeligheid koemelkeiwitvrij (nr.21)',
    },
    {
      value: DieetAandoening.VOEDSELOVERGEVOELIGHEID_KIPPENEI_EIWITVRIJ,
      label: 'Voedselovergevoeligheid kippenei eiwitvrij (nr.22)',
    },
    {
      value: DieetAandoening.VOEDSELOVERGEVOELIGHEID_LACTOSEBEPERKT_LACTOSEVRIJ,
      label: 'Voedselovergevoeligheid lactosebeperkt lactosevrij (nr. 4)',
    },
    {
      value: DieetAandoening.VOEDSELOVERGEVOELIGHEID_TARWEVRIJ,
      label: 'Voedselovergevoeligheid tarwevrij (nr.23)',
    },
    {
      value: DieetAandoening.VOEDSELOVERGEVOELIGHEID_TARWEVRIJ_ICM_KIPPENEI_EIWITVRIJ,
      label: 'Voedselovergevoeligheid tarwevrij icm kippenei eiwitvrij (nr.24)',
    },
    {
      value: DieetAandoening.VOEDSELOVERGEVOELIGHEID_KOEMELKEIWITVRIJ_ICM_KIPPENEI_EIWITVRIJ,
      label: 'Voedselovergevoeligheid koemelkeiwitvrij icm kippenei eiwitvrij (nr.25)',
    },
    {
      value: DieetAandoening.VOEDSELOVERGEVOELIGHEID_KOEMELKEIWITVRIJ_ICM_SOJA_EIWITVRIJ,
      label: 'Voedselovergevoeligheid koemelkeiwitvrij icm soja eiwitvrij (nr.26)',
    },
    {
      value: DieetAandoening.VOEDSELOVERGEVOELIGHEID_KOEMELKEIWITVRIJ_ICM_KIPPENEI_EIWITVRIJ_EN_SOJA_EIWITVRIJ,
      label: 'Voedselovergevoeligheid koemelkeiwitvrij icm kippenei eiwitvrij en soja eiwitvrij (nr.27)',
    },
    {
      value: DieetAandoening.VOEDSELOVERGEVOELIGHEID_KOEMELKEIWITVRIJ_ICM_KIPPENEI_EIWITVRIJ_EN_SOJA_EIWITVRIJ_EN_TARWEVRIJ,
      label: 'Voedselovergevoeligheid koemelkeiwitvrij icm kippenei eiwitvrij en soja eiwitvrij en tarwevrij (nr.28)',
    },
    {
      value: DieetAandoening.VOEDSELOVERGEVOELIGHEID_KOEMELKEIWITVRIJ_ICM_GLUTENVRIJ_EN_AL_DAN_NIET_TARWEVRIJ,
      label: 'Voedselovergevoeligheid koemelkeiwitvrij icm glutenvrij en al dan niet tarwevrij (nr.29)',
    },
    {
      value: DieetAandoening.BRANDWONDEN_ENGERIEVERRIJKT_IN_COMBINATIE_MET_EIWITVERRIJKT,
      label: 'Brandwonden energieverrijkt in combinatie met eiwitverrijkt (nr. 1)',
    },
    {
      value: DieetAandoening.LYMFE_LEKKAGE_EIWITVERRIJKT_IN_COMBINATIE_MET_STERK_LCT_VETBEPERKT_EN_MCT_VETVERRIJKT,
      label: 'Lymfe lekkage eiwitverrijkt in combinatie met sterk lct vetbeperkt en mct vetverrijkt (nr.30)',
    },
    {
      value: DieetAandoening.EPILEPSIE_STERK_KOOLHYDRAADBEPERKT_ICM_EIWITBEPERKT_EN_VETVERRIJKT,
      label: 'Epilepsie sterk koolhydraatbeperkt icm eiwitbeperkt en vetverrijkt (nr.31)',
    },
  ];

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> = this.dataService.incomeTaxEntity$.pipe(
    filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
    takeUntilDestroyed(this.destroyRef),
    shareReplay({bufferSize: 1, refCount: true}),
  );

  public ngOnInit(): void {
    this.incomeTaxEntity$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      this.updateForm(data.aangifte?.persoonsgebondenAftrek?.pgaSpecifiekeZorgkosten?.dieten || []);
    });
    this.subscribeToChanges();
  }

  ngOnDestroy(): void {
    this.changeSubscriptions.unsubscribe();
  }

  private subscribeToChanges(): void {
    this.changeSubscriptions.unsubscribe();
    this.changeSubscriptions = new Subscription();

    const extractRelevant = () =>
      this.dieten.getRawValue().map(
        (w): Dieet => ({
          aandoening: w.aandoening,
          periode: {
            begindatum: w.periode.begindatum,
            einddatum: w.periode.einddatum,
          },
        }),
      );

    this.changeSubscriptions.add(
      this.dieten.valueChanges
        .pipe(
          debounceTime(500),
          tap(() => this.saveForm()),
          map(() => extractRelevant()),
          distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
          switchMap(() => this.dataService.calculateLatestIncomeTaxReturn()),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(),
    );
  }

  get dieten(): FormArray {
    return this.formGroup.controls.dieten;
  }

  addRij(): void {
    this.dieten.push(this.createFormGroup());
  }

  verwijderRij(index: number): void {
    this.dieten.removeAt(index, {emitEvent: false});
    this.formGroup.updateValueAndValidity({emitEvent: false});
    this.formGroup.markAsDirty({emitEvent: false});
    this.saveForm();
  }

  createFormGroup(dieet?: Dieet): FormGroup {
    return new FormGroup({
      aandoening: new FormControl<DieetAandoening | null>(dieet?.aandoening ?? null),
      periode: new FormGroup({
        begindatum: new FormControl<Date | null>(dieet?.periode?.begindatum ?? null),
        einddatum: new FormControl<Date | null>(dieet?.periode?.einddatum ?? null),
      }),
    });
  }

  private updateForm(data: Dieet[]): void {
    const lijst = data || [];
    const formArray = this.dieten;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        control.patchValue(lijst[i], {emitEvent: false});
      } else {
        const formGroup = this.createFormGroup(lijst[i]);
        formArray.push(formGroup, {emitEvent: false});
      }
    }
  }

  saveForm(): void {
    const dieten = this.formGroup
      .getRawValue()
      .dieten.filter((dieet) => dieet.aandoening || dieet.periode)
      .map((dieten) => ({
          aandoening: dieten.aandoening,
          periode: dieten.periode,
        }) as Dieet,
      );

    this.dataService.patchIncomeTaxEntity((currentEntity) => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          persoonsgebondenAftrek: {
            ...currentEntity.aangifte.persoonsgebondenAftrek,
            pgaSpecifiekeZorgkosten: {
              ...currentEntity.aangifte.persoonsgebondenAftrek?.pgaSpecifiekeZorgkosten,
              dieten: dieten,
            },
          },
        },
      } as IncomeTaxEntity;
    });
  }
}
